import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
// import { useSelector } from "react-redux";
import { useSelector } from "react-redux";
import Table from "../../core/pagination/datatable";
import CommonFooter from "../../core/common/footer/commonFooter";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";

import DefaultEditor from "react-simple-wysiwyg";
import Select from "react-select";
import CommonDeleteModal from "../../core/common/modal/commonDeleteModal";
import {
  createSubCategory,
  getAllSubCategories,
  updateSubCategory,
  deleteSubCategory,
} from "../../api/subCategoryApi";
import { api } from "../../api/client";
import { uploadCatalogImage } from "../../api/productApi";
import { toast } from "react-toastify";
import { getCurrentUserPerms, hasWrite, isCurrentUserAdmin } from "../../utils/perm";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DEFAULT_SUBCATEGORY_IMAGE = "/assets/img/by-default.png";

// ---------- helpers (same convention as CategoryList) ----------
const normalizeKey = (s) => {
  if (!s) return "";
  let v = String(s).trim();
  v = v.replace(/^\/+/, "");
  v = v.replace(/^catelog\//i, "catalog/"); // fix legacy
  if (!/^catalog\//i.test(v)) {
    v = `catalog/${v.split("/").pop()}`;
  }
  return v;
};

const toPublicUrl = (v) => {
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v; // already URL
  const key = normalizeKey(v);
  const base = (
    process.env.REACT_APP_API_IMAGE_BASE_URL ||
    "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
  ).replace(/\/+$/, "");
  return `${base}/${key}`;
};
// --------------------------------------------------------------

const SubCategories = () => {
  const user = useSelector((state) => state?.auth?.user);
  const userPerms = getCurrentUserPerms(user?.permissions || []);
  const canWriteSubCategories =
    isCurrentUserAdmin(user) ||
    userPerms.includes("*") ||
    hasWrite(userPerms, "subCategory");

  // const dataSource = useSelector((state) => state.rootReducer.subcategory_data);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subCategoryEN, setSubCategoryEN] = useState("");
  const [subCategoryAR, setSubCategoryAR] = useState("");
  const [categoryCode, setCategoryCode] = useState("");
  const [descriptionEN, setDescriptionEN] = useState("");
  const [descriptionAR, setDescriptionAR] = useState("");
  const [status, setStatus] = useState(true);

  // CREATE modal image state
  const [imageKey, setImageKey] = useState(""); // "catalog/..."
  const [imagePreview, setImagePreview] = useState(""); // public URL for UI

  // EDIT modal image state
  const [editImageKey, setEditImageKey] = useState(""); // "catalog/..."
  const [editImagePreview, setEditImagePreview] = useState(""); // public URL for UI

  const [subcategoryList, setSubcategoryList] = useState([]);

  const [editSubCategoryId, setEditSubCategoryId] = useState(null);
  const [editSubCategoryEN, setEditSubCategoryEN] = useState("");
  const [editSubCategoryAR, setEditSubCategoryAR] = useState("");
  const [editCategoryCode, setEditCategoryCode] = useState("");
  const [editDescriptionEN, setEditDescriptionEN] = useState("");
  const [editDescriptionAR, setEditDescriptionAR] = useState("");
  const [editStatus, setEditStatus] = useState(true);
  const [editSelectedCategory, setEditSelectedCategory] = useState(null);
  const [deleteSubCategoryId, setDeleteSubCategoryId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingSubcategories, setLoadingSubcategories] = useState(true);

  // fetch subcategories
  useEffect(() => {
    const fetchSubCategories = async () => {
      try {
        setLoadingSubcategories(true); // 👈 start loading
        const data = await getAllSubCategories();
        setSubcategoryList(data || []);
      } catch (error) {
        console.error("Failed to load subcategories", error);
      } finally {
        setLoadingSubcategories(false); // 👈 stop loading
      }
    };
    fetchSubCategories();
  }, []);

  // fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get(
          `${API_BASE_URL}api/categories/category-list`
        );
        const formatted = res.data.map((cat) => ({
          value: cat.id,
          label: cat.name,
        }));
        setCategoryOptions(formatted);
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, []);

  // CREATE modal: upload to S3, store key, show S3 preview
  const handleCreateImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const dataUrl = reader.result;

        // quick local preview
        setImagePreview(dataUrl);

        // deterministic filename
        const ext =
          (file.type && file.type.split("/")[1]) ||
          file.name.split(".").pop() ||
          "jpg";
        const stamp = new Date()
          .toISOString()
          .replace(/[-:.TZ]/g, "")
          .slice(0, 14);
        const base = (subCategoryEN || "subcategory")
          .toString()
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9-_]/g, "");
        const desiredName = `SUB_${base || "subcategory"}_${stamp}.${ext}`;

        const { key } = await uploadCatalogImage(dataUrl, desiredName);

        const k = normalizeKey(key);
        setImageKey(k);
        setImagePreview(toPublicUrl(k)); // switch to S3 URL
      } catch (err) {
        console.error(err);
        toast.error(window.translate("Image upload failed"));
        setImageKey("");
        setImagePreview("");
      }
    };
    reader.readAsDataURL(file);
  };

  // EDIT modal: upload to S3, store key, show S3 preview
  const handleEditImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const dataUrl = reader.result;

        // quick local preview
        setEditImagePreview(dataUrl);

        // deterministic filename
        const ext =
          (file.type && file.type.split("/")[1]) ||
          file.name.split(".").pop() ||
          "jpg";
        const stamp = new Date()
          .toISOString()
          .replace(/[-:.TZ]/g, "")
          .slice(0, 14);
        const base = (editSubCategoryEN || "subcategory")
          .toString()
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9-_]/g, "");
        const desiredName = `SUB_${base || "subcategory"}_${stamp}.${ext}`;

        const { key } = await uploadCatalogImage(dataUrl, desiredName);

        const k = normalizeKey(key);
        setEditImageKey(k);
        setEditImagePreview(toPublicUrl(k));
      } catch (err) {
        console.error(err);
        toast.error(window.translate("Image upload failed"));
        setEditImageKey("");
        setEditImagePreview("");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!canWriteSubCategories) {
      toast.error(`❌ ${window.translate("You don't have permission to modify sub categories")}`);
      return;
    }
    if (isSubmitting) return; // prevent re-clicks
    setIsSubmitting(true);

    try {
      const duplicate = subcategoryList.find(
        (item) =>
          item.category?.toLowerCase().trim() ===
          subCategoryEN.toLowerCase().trim()
      );

      if (duplicate) {
        toast.warn("❌ Subcategory with this name already exists.");
        setIsSubmitting(false);
        return;
      }

      if (!imageKey) {
        toast.warn(window.translate("Please upload a subcategory image."));
        setIsSubmitting(false);
        return;
      }

      const payload = {
        parentCategory: selectedCategory?.value,
        subCategoryName: {
          en: subCategoryEN,
          ar: subCategoryAR,
        },
        slug: {
          en: subCategoryEN.toLowerCase().replace(/\s+/g, "-"),
          ar: subCategoryAR.toLowerCase().replace(/\s+/g, "-"),
        },
        categoryCode,
        description: {
          en: descriptionEN,
          ar: descriptionAR,
        },
        image: normalizeKey(imageKey), // store key only
        status: status ? "active" : "inactive",
      };

      await createSubCategory(payload);
      toast.success(` ${window.translate("Subcategory created")}`);

      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (err) {
      console.error("Failed to create subcategory:", err);
      toast.error(`${window.translate("Error creating subcategory")} ❌`);
      setIsSubmitting(false);
    }
  };

  // populate edit form
  const populateEditForm = (record) => {
    setEditSubCategoryId(record.id);

    // localized fields if available
    setEditSubCategoryEN(record.subCategoryName?.en || record.category || "");
    setEditSubCategoryAR(record.subCategoryName?.ar || "");

    setEditCategoryCode(record.categorycode || "");

    setEditDescriptionEN(record.description?.en || "");
    setEditDescriptionAR(record.description?.ar || "");

    const k = normalizeKey(record.img || "");
    setEditImageKey(k);
    setEditImagePreview(toPublicUrl(k) || DEFAULT_SUBCATEGORY_IMAGE);
    setEditStatus(record.status === "active");

    setEditSelectedCategory({
      value: record.parentCategory,
      label: record.parentCategoryName,
    });
  };

  // handle edit form submission
  const handleEditSubmit = async () => {
    if (!canWriteSubCategories) {
      toast.error(`❌ ${window.translate("You don't have permission to modify sub categories")}`);
      return;
    }
    try {
      const payload = {
        parentCategory: editSelectedCategory?.value,
        subCategoryName: {
          en: editSubCategoryEN,
          ar: editSubCategoryAR,
        },
        slug: {
          en: editSubCategoryEN.toLowerCase().replace(/\s+/g, "-"),
          ar: editSubCategoryAR.toLowerCase().replace(/\s+/g, "-"),
        },
        categoryCode: editCategoryCode,
        description: {
          en: editDescriptionEN,
          ar: editDescriptionAR,
        },
        image: normalizeKey(editImageKey), // store key only
        status: editStatus ? "active" : "inactive",
      };

      await updateSubCategory(editSubCategoryId, payload);
      toast.success(` ${window.translate("Subcategory updated")}`);

      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (err) {
      console.error("Error updating subcategory:", err);
      toast.error(`❌ ${window.translate("Failed to update subcategory")}`);
    }
  };

  const handleDelete = async () => {
    if (!canWriteSubCategories) {
      toast.error(`❌ ${window.translate("You don't have permission to modify sub categories")}`);
      return;
    }
    try {
      await deleteSubCategory(deleteSubCategoryId);
      toast.success(` ${window.translate("Subcategory deleted")}`);
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (err) {
      console.error("Error deleting subcategory:", err);
      toast.error(`❌ ${window.translate("Failed to delete subcategory")}`);
    }
  };

  const columns = [
    {
      title: window.translate("Image"),
      dataIndex: "img",
      render: (img) => (
        <span className="productimgname">
          <Link to="#" className="product-img stock-img">
            <img
              src={toPublicUrl(img) || DEFAULT_SUBCATEGORY_IMAGE}
              alt="Subcategory"
              style={{ width: "80px", height: "auto", borderRadius: "4px" }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = DEFAULT_SUBCATEGORY_IMAGE;
              }}
            />
          </Link>
        </span>
      ),
    },
    {
      title: window.translate("Sub Category"),
      dataIndex: "category", // shows subcategory name
      sorter: (a, b) => a.category.localeCompare(b.category),
    },
    {
      title: window.translate("Category"),
      dataIndex: "parentCategoryName", // shows parent category name
      sorter: (a, b) =>
        a.parentCategoryName.localeCompare(b.parentCategoryName),
    },
    {
      title: window.translate("Category Code"),
      dataIndex: "categorycode",
      sorter: (a, b) => a.categorycode.localeCompare(b.categorycode),
    },
    {
      title: window.translate("Description"),
      dataIndex: "description",
      render: (desc) => (
        <span>
          {desc?.[localStorage.getItem("lang") || "en"]?.substring(0, 60)}...
        </span>
      ),
      sorter: (a, b) =>
        (
          a.description?.[localStorage.getItem("lang") || "en"] || ""
        ).localeCompare(
          b.description?.[localStorage.getItem("lang") || "en"] || ""
        ),
    },
    {
      title: window.translate("Status"),
      dataIndex: "status",
      render: (text) => (
        <span
          className={`badge fw-medium fs-10 ${
            text === "active" ? "bg-success" : "bg-danger"
          }`}
        >
          {text}
        </span>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      render: (text, record) =>
        canWriteSubCategories ? (
          <div className="action-table-data">
            <div className="edit-delete-action">
              <Link
                className="me-2 p-2"
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#edit-category"
                onClick={() => populateEditForm(record)}
              >
                <i data-feather="edit" className="feather-edit"></i>
              </Link>
              <Link
                data-bs-toggle="modal"
                data-bs-target="#delete-modal"
                className="p-2"
                to="#"
                onClick={() => setDeleteSubCategoryId(record.id)}
              >
                <i data-feather="trash-2" className="feather-trash-2"></i>
              </Link>
            </div>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4 className="fw-bold">{window.translate("Sub Category")}</h4>
                <h6>{window.translate("Manage your sub categories")}</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <RefreshIcon />
              <CollapesIcon />
            </ul>
            {canWriteSubCategories && (
              <div className="page-btn">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add-category"
                >
                  <i className="ti ti-circle-plus me-2 ms-2"></i>{" "}
                  {window.translate("Add Sub Category")}
                </Link>
              </div>
            )}
          </div>

          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    Category
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Computers
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Electronics
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Shoe
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Electronics
                      </Link>
                    </li>
                  </ul>
                </div>

                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    {window.translate("status")}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("active")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        In{window.translate("active")}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="card-body">
              <div className="table-responsive sub-category-table">
                {loadingSubcategories ? (
                  // 🔄 Spinner while loading subcategories
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mb-0 mt-3 text-muted fw-medium">
                        {window.translate("Loading sub categories...")}
                      </p>
                    </div>
                  </div>
                ) : subcategoryList.length > 0 ? (
                  // ✅ Data loaded: show table
                  <Table columns={columns} dataSource={subcategoryList} />
                ) : (
                  // 📝 No data: centered message
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <p className="mb-0 text-muted fw-medium text-center">
                      {window.translate(
                        "Loading sub categories or no data available"
                      )}
                      .
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>

      <>
        {/* Add Subcategory */}
        <div className="modal fade" id="add-category">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="page-wrapper-new p-0">
                <div className="content">
                  <div className="modal-header">
                    <div className="page-title">
                      <h4>{window.translate("Add Sub Category")}</h4>
                    </div>
                    <button
                      type="button"
                      className="close bg-danger text-white fs-16"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  </div>
                  <div className="modal-body">
                    <form>
                      <div className="mb-3">
                        <div className="add-image-upload">
                          <div className="add-image p-1 border-solid position-relative">
                            {imagePreview ? (
                              <>
                                <img
                                  src={imagePreview}
                                  alt="Preview"
                                  style={{
                                    width: "100px",
                                    height: "100px",
                                    objectFit: "cover",
                                    borderRadius: "6px",
                                    border: "1px solid #ccc",
                                  }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = DEFAULT_SUBCATEGORY_IMAGE;
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setImageKey("");
                                    setImagePreview("");
                                  }}
                                  className="btn btn-sm btn-danger position-absolute"
                                  style={{
                                    top: "5px",
                                    right: "5px",
                                    borderRadius: "50%",
                                    padding: "0 6px",
                                  }}
                                  title="Remove image"
                                >
                                  ×
                                </button>
                              </>
                            ) : (
                              <span className="fw-normal">
                                <i
                                  data-feather="plus-circle"
                                  className="plus-down-add"
                                />{" "}
                                {window.translate("Add Image")}
                              </span>
                            )}
                          </div>

                          <div className="new-employee-field">
                            <div className="mb-0">
                              <div className="image-upload mb-2">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleCreateImageChange}
                                />
                                <div className="image-uploads">
                                  <h4 className="fs-13 fw-medium">
                                    {window.translate("Upload Image")}
                                  </h4>
                                </div>
                              </div>
                              <span>JPEG, PNG up to 2 MB</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Category")}
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <Select
                          classNamePrefix="react-select"
                          options={categoryOptions}
                          placeholder="Choose"
                          value={selectedCategory}
                          onChange={setSelectedCategory}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Sub Category")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control mb-2"
                          placeholder="Subcategory EN"
                          value={subCategoryEN}
                          onChange={(e) => setSubCategoryEN(e.target.value)}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Sub Category")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control mb-2"
                          placeholder="Subcategory AR"
                          value={subCategoryAR}
                          onChange={(e) => setSubCategoryAR(e.target.value)}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Category Code")}
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={categoryCode}
                          onChange={(e) => setCategoryCode(e.target.value)}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Description")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <DefaultEditor
                          value={descriptionEN}
                          onChange={(e) => setDescriptionEN(e.target.value)}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Description")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <DefaultEditor
                          value={descriptionAR}
                          onChange={(e) => setDescriptionAR(e.target.value)}
                        />
                      </div>

                      <div className="mb-0">
                        <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                          <span className="status-label">
                            {window.translate("status")}
                          </span>
                          <input
                            type="checkbox"
                            id="user2"
                            className="check"
                            checked={status}
                            onChange={() => setStatus(!status)}
                          />
                          <label htmlFor="user2" className="checktoggle" />
                        </div>
                      </div>
                    </form>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                      data-bs-dismiss="modal"
                    >
                      {window.translate("cancel")}
                    </button>
                    <Link
                      to="#"
                      className="btn btn-primary fs-13 fw-medium p-2 px-3"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? window.translate("Creating...")
                        : window.translate("Create Subcategory")}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Subcategory */}
        <div className="modal fade" id="edit-category">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="page-wrapper-new p-0">
                <div className="content">
                  <div className="modal-header">
                    <div className="page-title">
                      <h4>{window.translate("Edit Sub Category")}</h4>
                    </div>
                    <button
                      type="button"
                      className="close bg-danger text-white fs-16"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  </div>

                  <div className="modal-body">
                    <form>
                      <div className="mb-3">
                        <div className="add-image-upload">
                          <div className="add-image p-1 border-solid position-relative">
                            {editImagePreview ? (
                              <>
                                <img
                                  src={editImagePreview}
                                  alt="Preview"
                                  style={{
                                    width: 100,
                                    height: 100,
                                    objectFit: "cover",
                                    borderRadius: 6,
                                    border: "1px solid #ccc",
                                  }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = DEFAULT_SUBCATEGORY_IMAGE;
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditImageKey("");
                                    setEditImagePreview("");
                                  }}
                                  className="btn btn-sm btn-danger position-absolute"
                                  style={{
                                    top: 5,
                                    right: 5,
                                    borderRadius: "50%",
                                    padding: "0 6px",
                                  }}
                                  title="Remove image"
                                >
                                  ×
                                </button>
                              </>
                            ) : (
                              <img
                                src={DEFAULT_SUBCATEGORY_IMAGE}
                                alt="Subcategory"
                                style={{
                                  width: 100,
                                  height: 100,
                                  objectFit: "cover",
                                  borderRadius: 6,
                                  border: "1px solid #ccc",
                                }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = DEFAULT_SUBCATEGORY_IMAGE;
                                }}
                              />
                            )}
                          </div>

                          <div className="new-employee-field">
                            <div className="mb-0">
                              <div className="image-upload mb-2">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleEditImageChange}
                                />
                                <div className="image-uploads">
                                  <h4 className="fs-13 fw-medium">
                                    {window.translate("Change Image")}
                                  </h4>
                                </div>
                              </div>
                              <span>JPEG, PNG up to 2 MB</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Category")}
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <Select
                          classNamePrefix="react-select"
                          options={categoryOptions}
                          placeholder="Choose"
                          value={editSelectedCategory}
                          onChange={setEditSelectedCategory}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Sub Category")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editSubCategoryEN}
                          onChange={(e) => setEditSubCategoryEN(e.target.value)}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Sub Category")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editSubCategoryAR}
                          onChange={(e) => setEditSubCategoryAR(e.target.value)}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Category Code")}
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editCategoryCode}
                          onChange={(e) => setEditCategoryCode(e.target.value)}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Description")} (EN)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <textarea
                          className="form-control"
                          value={editDescriptionEN}
                          onChange={(e) => setEditDescriptionEN(e.target.value)}
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          {window.translate("Description")} (AR)
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <textarea
                          className="form-control"
                          value={editDescriptionAR}
                          onChange={(e) => setEditDescriptionAR(e.target.value)}
                        />
                      </div>

                      <div className="mb-0">
                        <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                          <span className="status-label">
                            {window.translate("status")}
                          </span>
                          <input
                            type="checkbox"
                            id="user3"
                            className="check"
                            defaultChecked
                            onChange={() => setEditStatus(!editStatus)}
                          />
                          <label htmlFor="user3" className="checktoggle" />
                        </div>
                      </div>
                    </form>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                      data-bs-dismiss="modal"
                    >
                      {window.translate("cancel")}
                    </button>
                    <Link
                      to="#"
                      className="btn btn-primary fs-13 fw-medium p-2 px-3"
                      onClick={handleEditSubmit}
                    >
                      {window.translate("Edit Sub Category")}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <CommonDeleteModal onConfirm={handleDelete} />
      </>
    </>
  );
};

export default SubCategories;
